import React, { useEffect, useState } from "react";
import { CustomTextInput } from "../../components/customTextInput";
import { Text, View } from "react-native";
import CustomCheckBoxWithProps from "../../components/checkBox/customCheckBox";
import CustomDropDown from "../../components/customDropdown/customDropdown";
import { useCssStyle } from "../../customHook/getCssStyle";
import CustomPressable from "../../components/customPressable/customPressable";
import { inAppBrowserFunc } from "../../js/inAppBrowser/inAppBrowser";

const ActivitiesDynamicForm = ({ question, answers, onAnswerChange, guestProfile }) => {
  const { id, fieldType, prompt, options, termsAndConditions, config } = question;
  const getStyles = useCssStyle();

  // Initialize state based on field type
  const getInitialValue = () => {
    if (fieldType === "multiple_select") return [];
    if (fieldType === "terms_and_conditions") return false;
    return "";
  };

  const [value, setValue] = useState(getInitialValue);

  // Sync with existing answers
  useEffect(() => {
    if (answers?.length) {
      const answerObj = answers.find((a) => a.questionId === id);
      
      if (answerObj) {
        if (fieldType === "multiple_select") {
          const values = answerObj.answer?.map((a) => a.key) || [];
          setValue(values);
        } else {
          setValue(answerObj.answer[0]?.value || getInitialValue());
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, id]);


  useEffect(() => {
    if (!value && guestProfile) {
      const idLower = id?.toLowerCase() || "";
      let prefillValue = null;

      if (idLower.includes("email")) {
        prefillValue = guestProfile.email;
      } else if (idLower.includes("phone")) {
        prefillValue = guestProfile.phone;
      } else if (idLower.includes("firstname")) {
        prefillValue = guestProfile.firstName;
      } else if (idLower.includes("lastname")) {
        prefillValue = guestProfile.lastName;
      } else if (idLower.includes("address")) {
        if (idLower.includes("address1") || idLower.includes("addressline1")) {
          prefillValue = guestProfile.addressLine1;
        } else if (idLower.includes("address2") || idLower.includes("addressline2")) {
          prefillValue = guestProfile.addressLine2;
        } else {
          prefillValue = guestProfile.addressLine1;
        }
      } else if (idLower.includes("city")) {
        prefillValue = guestProfile.city;
      } else if (idLower.includes("state")) {
        prefillValue = guestProfile.state;
      } else if (idLower.includes("country")) {
        prefillValue = guestProfile.country;
      } else if (idLower.includes("birthday") || idLower.includes("dob") || idLower.includes("dateofbirth")) {
        prefillValue = guestProfile.birthday;
      } else if (idLower.includes("title")) {
        prefillValue = guestProfile.title;
      }

      if (prefillValue) {
        setValue(prefillValue);
        onAnswerChange({
          questionId: id,
          answer: [{ value: prefillValue, key: null }]
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPrefillValue = () => {
    return value || "";
  };

  const handleInputChange = (newValue) => {
    setValue(newValue);
    onAnswerChange({
      questionId: id,
      answer: [{ value: newValue, key: null }]
    });
  };

  const handleSingleSelectChange = (e) => {
    const selected = options.find((opt) => opt.key === e.value);
    const selectedKey = selected?.key || "";
    
    setValue(selectedKey);
    onAnswerChange({
      questionId: id,
      answer: selected ? [{ key: selected.key, value: selected.value }] : []
    });
  };

  const handleCheckboxChange = (option) => {
    const currentValues = value || [];
    const isChecked = currentValues.includes(option.key);
    
    const updatedValues = isChecked
      ? currentValues.filter((k) => k !== option.key)
      : [...currentValues, option.key];

    setValue(updatedValues);
    onAnswerChange({
      questionId: id,
      answer: options
        .filter((opt) => updatedValues.includes(opt.key))
        .map((opt) => ({ key: opt.key, value: opt.value }))
    });
  };

  const handleTermsChange = () => {
    const newValue = !value;
    setValue(newValue);
    onAnswerChange({
      questionId: id,
      answer: [{ value: newValue, key: null }]
    });
  };

  const mandatoryMark = config?.isRequired ? (
    <Text style={{ color: "red" }}>*</Text>
  ) : null;

  if (config?.disabled && (fieldType === "input" || fieldType === "textarea")) {
    return null;
  }

  switch (fieldType) {
    case "email":
      return (
        <View style={getStyles(["textContainer"])}>
          <Text style={getStyles(["label"])}>
            {prompt} {mandatoryMark}
          </Text>
          <CustomTextInput
            type="text"
            value={value}
            disabled={config?.disabled}
            onChangeText={handleInputChange}
            keyboardType="email-address"
            autoCapitalize="none"
          />
        </View>
      );

    case "phone":
      return (
        <View style={getStyles(["textContainer"])}>
          <Text style={getStyles(["label"])}>
            {prompt} {mandatoryMark}
          </Text>
          <CustomTextInput
            type="text"
            value={value}
            disabled={config?.disabled}
            onChangeText={handleInputChange}
            keyboardType="phone-pad"
          />
        </View>
      );

    case "input":
    case "textarea":
      return (
        <View style={getStyles(["textContainer"])}>
          <Text style={getStyles(["label"])}>
            {prompt} {mandatoryMark}
          </Text>
          <CustomTextInput
            type="text"
            value={getPrefillValue()}
            disabled={config?.disabled}
            onChangeText={handleInputChange}
            multiline={fieldType === "textarea"}
            numberOfLines={fieldType === "textarea" ? 4 : 1}
          />
        </View>
      );

    case "single_select":
      return (
        <View style={getStyles(["dropdownContainer"])} id="dropdownContainer">
          <CustomDropDown
            label={prompt}
            placeholder="PLEASE SELECT"
            isRequired={mandatoryMark}
            options={options?.map((opt) => ({
              value: opt.key,
              label: opt.value
            })) || []}
            selectedOption={value}
            onSelectOption={handleSingleSelectChange}
          />
        </View>
      );

    case "multiple_select":
      return (
        <View style={getStyles(["textContainer"])} id="textContainer">
          <Text style={getStyles(["label"])}>
            {prompt} {mandatoryMark}
          </Text>
          {options?.map((opt) => (
            <View
              key={opt.key}
              style={getStyles(["checkboxContainer"])}
              id="checkboxContainer"
            >
              <CustomCheckBoxWithProps
                style={getStyles(["checkbox"])}
                id="checkbox"
                value={value?.includes(opt.key) || false}
                onValueChange={() => handleCheckboxChange(opt)}
              />
              <Text
                style={getStyles(["checkboxContainerText"])}
                id="checkboxContainerText"
              >
                {opt.value}
              </Text>
            </View>
          ))}
        </View>
      );

    case "terms_and_conditions":
      return (
        <View
          style={getStyles(["termnsAndConditionContainer"])}
          id="termnsAndConditionContainer"
        >
          <CustomCheckBoxWithProps
            id="termnsAndConditionCheckbox"
            style={getStyles(["termnsAndConditionCheckbox"])}
            value={value}
            onValueChange={handleTermsChange}
          />
          <Text
            style={getStyles(["termsAndConditionCheckboxText"])}
            id="termsAndConditionCheckboxText"
          >
            I agree to{" "}
          </Text>
          <CustomPressable
            id="termsAndConditionContainer"
            style={getStyles(["termsAndConditionContainer"])}
            onPress={() =>
              inAppBrowserFunc(termsAndConditions?.mediaURL || "")
            }
          >
            <Text
              id="termsAndConditionText"
              style={getStyles(["termsAndConditionText"])}
            >
              {termsAndConditions?.label || "Terms and Conditions"}
            </Text>
          </CustomPressable>
        </View>
      );

    default:
      return null;
  }
};

export default ActivitiesDynamicForm;
